import React from "react";
import { clsx } from "clsx";
import { AlertCircle, AlertTriangle, CheckCircle2, Info, X } from "lucide-react";

export type AlertTone = "info" | "success" | "warning" | "danger";

export interface AlertProps extends React.HTMLAttributes<HTMLDivElement> {
    tone?: AlertTone;
    title?: string;
    onDismiss?: () => void;
}

const toneBox: Record<AlertTone, string> = {
    info: "border-secondary/40 bg-secondary/10 text-neutral-dark dark:text-white",
    success: "border-success/40 bg-success/10 text-neutral-dark dark:text-white",
    warning: "border-warning/40 bg-warning/10 text-neutral-dark dark:text-white",
    danger: "border-danger/40 bg-danger/10 text-neutral-dark dark:text-white",
};

const icons = {
    info: Info,
    success: CheckCircle2,
    warning: AlertTriangle,
    danger: AlertCircle,
};

export const Alert: React.FC<AlertProps> = ({
    tone = "info",
    title,
    onDismiss,
    className,
    children,
    ...props
}) => {
    const Icon = icons[tone];
    return (
        <div
            role="alert"
            className={clsx("flex gap-3 rounded-mz-xl border px-4 py-3", toneBox[tone], className)}
            {...props}
        >
            <Icon className="mt-0.5 h-5 w-5 shrink-0" aria-hidden />
            <div className="min-w-0 flex-1 text-sm">
                {title ? <p className="mb-0.5 font-semibold">{title}</p> : null}
                <div className="text-neutral-secondary dark:text-slate-300">{children}</div>
            </div>
            {onDismiss ? (
                <button
                    type="button"
                    onClick={onDismiss}
                    className="rounded-mz-sm p-1 hover:bg-black/5 dark:hover:bg-white/10"
                    aria-label="Dismiss"
                >
                    <X size={16} />
                </button>
            ) : null}
        </div>
    );
};
